import { Component, OnInit, ViewEncapsulation, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { finalize } from 'rxjs/operators';
import { BillingService } from 'app/services/billing/billing.service';
import { AvailablePlan, UpdateSubscriptionPayload } from 'app/services/billing/billing.type';
import { AlertService } from 'app/components/ui/alert/alert.service';
import { ConfirmationModalComponent } from './confirmation-modal.component';
import { MessagesConstants } from 'app/constants/messages.constants';

export interface UpdatePlanModalData {
    currentPlan?: {
        id?: number;
        name: string;
        stripePriceId?: string;
    };
    memberCount?: number;
}

@Component({
    selector: 'update-plan-modal',
    templateUrl: './update-plan-modal.component.html',
    styleUrls: ['./update-plan-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        MatProgressSpinnerModule
    ]
})
export class UpdatePlanModalComponent implements OnInit {
    plans: AvailablePlan[] = [];
    selectedPlan: AvailablePlan | null = null;
    isLoading: boolean = true;
    isUpdating: boolean = false;
    currentPlan: { id?: number; name: string; stripePriceId?: string } | null = null;
    memberCount: number = 0;

    constructor(
        public dialogRef: MatDialogRef<UpdatePlanModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UpdatePlanModalData,
        private _billingService: BillingService,
        private _alertService: AlertService,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        // Se os dados não foram passados, buscar da API
        if (!this.data.currentPlan) {
            this.loadSubscriptionData();
        } else {
            this.currentPlan = this.data.currentPlan;
            this.memberCount = this.data.memberCount || 0;
            this.loadPlans();
        }
    }

    loadSubscriptionData(): void {
        this.isLoading = true;

        this._billingService.getSubscription()
            .subscribe({
                next: (response) => {
                    const billingData = response.data;

                    if (!billingData?.plan) {
                        this._alertService.error(MessagesConstants.BILLING_SUBSCRIPTION_DATA_UNAVAILABLE, { autoClose: true });
                        this.dialogRef.close();
                        return;
                    }

                    this.currentPlan = {
                        id: billingData.plan.id,
                        name: billingData.plan.name,
                        stripePriceId: billingData.plan.stripePriceId
                    };
                    this.memberCount = billingData.church?.memberCount || 0;

                    this.loadPlans();
                },
                error: (response) => {
                    this.isLoading = false;
                    this._alertService.error(
                        response?.error?.message || MessagesConstants.BILLING_SUBSCRIPTION_DATA_ERROR,
                        { autoClose: true }
                    );
                    this.dialogRef.close();
                }
            });
    }

    loadPlans(): void {
        this._billingService.getPlans()
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (response) => {
                    this.plans = response.data;
                },
                error: (response) => {
                    this._alertService.error(
                        response?.error?.message || MessagesConstants.BILLING_PLANS_LOADING_ERROR,
                        { autoClose: true }
                    );
                }
            });
    }

    selectPlan(plan: AvailablePlan): void {
        // Não permite selecionar o plano atual (exceto quando é no_subscription)
        if (this.currentPlan &&
            this.currentPlan.name?.toLowerCase() !== 'no_subscription' &&
            this.currentPlan.stripePriceId &&
            plan.stripePriceId === this.currentPlan.stripePriceId) {
            return;
        }
        this.selectedPlan = plan;
    }

    isCurrentPlan(plan: AvailablePlan): boolean {
        // Se o plano atual for no_subscription, nenhum plano é considerado atual
        if (this.currentPlan?.name?.toLowerCase() === 'no_subscription') {
            return false;
        }
        return this.currentPlan?.stripePriceId ? plan.stripePriceId === this.currentPlan.stripePriceId : false;
    }

    updatePlan(): void {
        if (!this.selectedPlan || this.isUpdating) {
            return;
        }

        // Abrir modal de confirmação
        const confirmDialogRef = this._dialog.open(ConfirmationModalComponent, {
            width: '500px',
            maxWidth: '95%',
            disableClose: false,
            autoFocus: false,
            data: {
                title: MessagesConstants.BILLING_UPDATE_PLAN_CONFIRMATION_TITLE,
                message: MessagesConstants.BILLING_UPDATE_PLAN_CONFIRMATION_MESSAGE,
                confirmButtonText: MessagesConstants.BUTTON_YES,
                cancelButtonText: MessagesConstants.BUTTON_NO
            }
        });

        confirmDialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.performUpdate();
            }
        });
    }

    performUpdate(): void {
        if (!this.selectedPlan) {
            return;
        }

        this.isUpdating = true;

        const payload: UpdateSubscriptionPayload = {
            price_id: this.selectedPlan.stripePriceId
        };

        this._billingService.updateSubscription(payload)
            .pipe(finalize(() => this.isUpdating = false))
            .subscribe({
                next: (response) => {
                    this._alertService.success(
                        response.message || MessagesConstants.BILLING_UPDATE_PLAN_SUCCESS,
                        { autoClose: true }
                    );
                    this.dialogRef.close({ action: 'updated', plan: this.selectedPlan });
                },
                error: (response) => {
                    this._alertService.error(
                        response?.error?.message || MessagesConstants.BILLING_UPDATE_PLAN_ERROR,
                        { autoClose: true }
                    );
                }
            });
    }

    closeModal(): void {
        this.dialogRef.close();
    }

    getPlanDisplayName(planName: string): string {
        const planNames: { [key: string]: string } = {
            'bronze': 'Bronze',
            'silver': 'Prata',
            'gold': 'Ouro',
            'diamond': 'Diamond'
        };
        return planNames[planName.toLowerCase()] || planName;
    }

    getPlanMedalColor(planName: string): string {
        const colors: { [key: string]: string } = {
            'bronze': 'text-orange-600',
            'silver': 'text-gray-400',
            'gold': 'text-yellow-500',
            'diamond': 'text-cyan-600'
        };
        return colors[planName.toLowerCase()] || 'text-gray-600';
    }

    getPlanBgColor(planName: string): string {
        const colors: { [key: string]: string } = {
            'bronze': 'bg-orange-50',
            'silver': 'bg-gray-50',
            'gold': 'bg-yellow-50',
            'diamond': 'bg-cyan-50'
        };
        return colors[planName.toLowerCase()] || 'bg-gray-50';
    }

    getPlanBorderColor(planName: string): string {
        const colors: { [key: string]: string } = {
            'bronze': 'border-orange-200',
            'silver': 'border-gray-200',
            'gold': 'border-yellow-200',
            'diamond': 'border-cyan-200'
        };
        return colors[planName.toLowerCase()] || 'border-gray-200';
    }

    formatPrice(plan: AvailablePlan): string {
        if (plan.billingUnit) {
            return `R$ ${plan.price.toFixed(2).replace('.', ',')} por membro`;
        }
        return `R$ ${plan.price.toFixed(2).replace('.', ',')}`;
    }

    formatImmediateCharge(amount: number): string {
        return `R$ ${amount.toFixed(2).replace('.', ',')}`;
    }

    getDowngradeMessage(): string {
        return MessagesConstants.BILLING_DOWNGRADE_NEXT_CYCLE;
    }
}
